/**
 * Smooth scroll utility functions
 */

/**
 * Scrolls smoothly to the specified element
 * @param elementId - The ID of the element to scroll to
 * @param offset - Optional offset from the top (in pixels)
 * @param duration - Animation duration in ms
 */
export const scrollToElement = (elementId: string, offset = 0, duration = 800): void => {
    const targetElement = document.getElementById(elementId);

    if (!targetElement) {
        console.warn(`Element with id "${elementId}" not found`);
        return;
    }

    const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - offset;
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    let startTime: number | null = null;

    const animation = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const easeInOutCubic = progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        window.scrollTo(0, startPosition + distance * easeInOutCubic);

        if (timeElapsed < duration) {
            requestAnimationFrame(animation);
        }
    };

    requestAnimationFrame(animation);
};

/**
 * Creates a scroll-to link that smoothly scrolls to the target element
 * @param target - The target element ID to scroll to
 * @param offset - Optional offset from the top (in pixels)
 */
export const handleSmoothScroll = (
    e: React.MouseEvent<HTMLAnchorElement>,
    target: string,
    offset = 0
): void => {
    e.preventDefault();
    scrollToElement(target, offset);
};

/**
 * Initialize smooth scrolling for all links with data-scroll attribute
 * Usage: <a href="#section-id" data-scroll>Scroll to Section</a>
 */
export const initSmoothScroll = (): void => {
    document.addEventListener('DOMContentLoaded', () => {
        const scrollLinks = document.querySelectorAll('a[data-scroll]');

        scrollLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const target = (link as HTMLAnchorElement).getAttribute('href');

                if (target && target.startsWith('#')) {
                    const targetId = target.substring(1);
                    scrollToElement(targetId);
                }
            });
        });
    });
}; 